import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { wsManager } from '../services/websocketManager';
import type { ConnectionStatus } from '../services/websocketManager';
import { ConnectionStatus as CS } from '../services/websocketManager';
import { updateSnapshotStatuses } from '../features/snapshots/snapshotsSlice';
import { makeOpportunityKey } from '../types/opportunity';
import { useOpportunities } from '../contexts/OpportunitiesContext';

// Update UI every 75ms (13.3 times/second) by reading from cache
// Increased from 30ms to reduce CPU load and prevent WebSocket issues
const UI_UPDATE_INTERVAL = 75;

export const useWebSocketConnection = () => {
  const dispatch = useDispatch();
  const { setOpportunities, setConnectionStatus: setContextConnectionStatus } = useOpportunities();
  const [status, setStatus] = useState<ConnectionStatus>(CS.DISCONNECTED);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [opportunityCount, setOpportunityCount] = useState(0);
  
  // Stats tracking
  const statsRef = useRef({ snapshots: 0, lastLog: Date.now() });
  
  useEffect(() => {
    console.log('[Hook useWebSocketConnection] Effect running - mounting component');
    console.log('[Hook useWebSocketConnection] Snapshot mode: UI updates every', UI_UPDATE_INTERVAL, 'ms');
    
    // Reset stats
    statsRef.current = { snapshots: 0, lastLog: Date.now() };
    
    // The wsManager is a singleton, so multiple connect() calls should be safe
    // The connect() method checks if already connected
    wsManager.connect();
    
    // Subscribe to status changes
    const unsubscribeStatus = wsManager.onStatusChange((newStatus) => {
      console.log('[Hook useWebSocketConnection] Status changed to:', newStatus);
      setStatus(newStatus);
      setReconnectAttempts(wsManager.getReconnectAttempts());
      setContextConnectionStatus(newStatus === CS.CONNECTED);
    });
    
    // Create ticker to read from cache and update UI
    const tickerInterval = setInterval(() => {
      try {
        const tickStart = performance.now();
        
        // Get pre-filtered list from cache (already sorted and filtered)
        const opportunities = wsManager.getCache().getFilteredList();
        
        const tickElapsed = performance.now() - tickStart;
        if (tickElapsed > 20) {
          console.warn('[Ticker] Slow tick:', tickElapsed.toFixed(2), 'ms');
        }
        
        // Update Context with the current snapshot (not Redux!)
        setOpportunities(opportunities);
        setOpportunityCount(wsManager.getCache().size); // Total count in cache
        
        // Update snapshot statuses to track which opportunities still exist
        const currentKeys = opportunities.map(opp => makeOpportunityKey(opp));
        dispatch(updateSnapshotStatuses(currentKeys));
        
        // Stats tracking
        statsRef.current.snapshots++;
        const now = Date.now();
        if (now - statsRef.current.lastLog >= 10000) { // Log every 10 seconds
          console.log('[Snapshot] UI update stats:', {
            snapshots: statsRef.current.snapshots,
            totalCacheSize: wsManager.getCache().size,
            filteredCount: opportunities.length,
            updateRate: `${(statsRef.current.snapshots / 10).toFixed(1)} snapshots/s`,
            lastTickTime: tickElapsed.toFixed(2) + 'ms'
          });
          statsRef.current.snapshots = 0;
          statsRef.current.lastLog = now;
        }
      } catch (error) {
        console.error('[Ticker] Error in ticker:', error);
      }
    }, UI_UPDATE_INTERVAL);
    
    console.log('[Hook useWebSocketConnection] Snapshot ticker started');
    
    // Cleanup on unmount - only unsubscribe, don't disconnect
    return () => {
      console.log('[Hook useWebSocketConnection] Effect cleanup - unmounting component');
      
      clearInterval(tickerInterval);
      unsubscribeStatus();
      
      // Note: We don't disconnect wsManager here as it's a singleton
      console.log('[Hook useWebSocketConnection] Cleanup complete');
    };
  }, [dispatch, setOpportunities, setContextConnectionStatus]);
  
  const reconnect = () => {
    wsManager.disconnect();
    setTimeout(() => wsManager.connect(), 100);
  };
  
  const setFilters = (minProfit?: number, exchanges?: number[], pairs?: string[]) => {
    wsManager.getCache().setFilter({ minProfit, exchanges, pairs });
    console.log('[Hook] Filters updated:', { minProfit, exchanges, pairs });
  };
  
  const resetFilters = () => {
    wsManager.getCache().resetFilter();
    console.log('[Hook] Filters reset - showing all opportunities');
  };
  
  const getFilters = () => {
    return wsManager.getCache().getFilter();
  };
  
  return {
    status,
    reconnectAttempts,
    opportunityCount,
    reconnect,
    setFilters,
    resetFilters,
    getFilters,
    isConnected: status === CS.CONNECTED,
    isReconnecting: status === CS.RECONNECTING,
  };
};